import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Card, Stack, Typography, Button, IconButton, Chip, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions, MenuItem,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TextField, CircularProgress, Alert, Menu, useMediaQuery,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  Close as CloseIcon, ChevronLeft, ChevronRight,
  Groups as CreditorsIcon, Print as PrintIcon, GridOn as ExcelIcon,
  Today as TodayIcon, FilterAlt as FilterIcon,
  Info as InfoIcon, ArrowDropDown as CaretIcon,
} from '@mui/icons-material';
import ExcelJS from 'exceljs/dist/exceljs.min.js';
import API from '../BaseURL';
import swal from '../utils/swal';

// ═══════════════════════════════════════════════════════════════════
//  "Who is owing, and since when?"
//
//  The Creditors button used to filter the marking sheet down to
//  learners with arrears. That answered HOW MUCH but never WHEN — and
//  when is the question a teacher is actually asked. "Kofi owes
//  thirty-six cedis" means nothing next to "Kofi has taken the meal on
//  credit Monday, Tuesday and Wednesday".
//
//  So it opens the period instead: a row per learner, a column per day,
//  the days they did not pay called out, and a running total. Same
//  colours as the marking sheet, because it is the same information
//  read sideways.
//
//  A learner can be on two or three collections at once — feeding AND
//  transport AND school fees — so each one gets its own line inside the
//  learner's block, and the owing figure is the lot added together.
// ═══════════════════════════════════════════════════════════════════

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const money = (n) => Number(n || 0).toLocaleString(undefined,
  { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const prettyType = (t) => String(t || '')
  .replace(/_+$/, '').replace(/_/g, ' ')
  .replace(/\b\w/g, (c) => c.toUpperCase());

const isoOf = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
                   + `-${String(d.getDate()).padStart(2, '0')}`;

const mondayOf = (date) => {
  const d = new Date(date);
  d.setDate(d.getDate() - ((d.getDay() + 6) % 7));
  d.setHours(0, 0, 0, 0);
  return d;
};
const addDays = (date, n) => { const d = new Date(date); d.setDate(d.getDate() + n); return d; };
const daysBetween = (a, b) =>
  Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / 86400000);

const dayLabel = (d) => {
  const x = new Date(`${d}T00:00:00`);
  return {
    dow: x.toLocaleDateString(undefined, { weekday: 'short' }),
    dm:  x.toLocaleDateString(undefined, { day: 'numeric', month: 'short' }),
  };
};
const rangeLabel = (from, to) => {
  const f = (d, y) => new Date(`${d}T00:00:00`).toLocaleDateString(undefined,
    { weekday: 'short', day: 'numeric', month: 'short', ...(y ? { year: 'numeric' } : {}) });
  return from === to ? f(from, true) : `${f(from, false)} – ${f(to, true)}`;
};

// A month of columns is the most this shape can carry, and the backend
// refuses beyond it too.
const MAX_DAYS = 31;

// The marking sheet's own words in the marking sheet's own colours, so
// nothing has to be translated in the teacher's head.
const CELL = {
  full:    { bg: '#e8f6ed', fg: '#15803d', line: '#b9e3ca', word: 'PAID' },
  partial: { bg: '#eaf2fb', fg: '#1c5b96', line: '#c6dcf1', word: 'PART' },
  none:    { bg: '#fdf2e0', fg: '#a1610a', line: '#f3ddb8', word: 'CREDIT' },
  absent:  { bg: '#fdeaea', fg: '#b91c1c', line: '#f6cccc', word: 'NOT EATEN' },
  advance: { bg: '#eef0fe', fg: '#4338ca', line: '#c7cffb', word: 'ADVANCE' },
};
const BLANK = { bg: '#f6f8fb', fg: '#b4bfcd', line: '#e8eef6', word: '·' };

// A day settled out of money already paid is not a debt, however the
// teacher marked it at the time.
const settledFromAdvance = (c) => !!c && c.status !== 'absent'
  && Number(c.credit_used || 0) > 0
  && (Number(c.daily_amount || 0) - Number(c.amount_paid || 0)
      - Number(c.credit_used || 0)) <= 0.009;

// What one day cost the learner, and what is still owed on it.
const readCell = (c, fallbackRate) => {
  if (!c) return null;
  const rate = Number(c.daily_amount || fallbackRate || 0);
  if (settledFromAdvance(c)) {
    return { tone: CELL.advance, shown: Number(c.credit_used || 0), short: 0, rate };
  }
  if (c.status === 'absent') {
    return { tone: CELL.absent, shown: 0, short: 0, rate };
  }
  const paid = Number(c.amount_paid || 0);
  const used = Number(c.credit_used || 0);
  const short = rate - paid - used;
  return {
    tone: CELL[c.status] || BLANK,
    shown: c.status === 'none' ? 0 : paid,
    short: short > 0.009 ? short : 0,
    rate,
  };
};

function Cell({ mark, rate }) {
  const r = readCell(mark, rate);
  if (!r) {
    return (
      <Box sx={{
        px: 0.4, py: 0.45, borderRadius: 1, textAlign: 'center', minWidth: 50,
        bgcolor: BLANK.bg, border: `1px solid ${BLANK.line}`,
      }}>
        <Typography sx={{ fontSize: 11, fontWeight: 700, color: BLANK.fg, lineHeight: 1.4 }}>
          {BLANK.word}
        </Typography>
      </Box>
    );
  }
  const owing = r.short > 0.009;
  return (
    <Tooltip title={
      r.tone === CELL.advance ? `Covered by advance — GH¢ ${money(r.shown)}`
      : r.tone === CELL.absent ? 'Did not take it — nothing owed'
      : owing ? `${r.tone.word} · GH¢ ${money(r.short)} still owed for this day`
                + (mark.notes ? ` · ${mark.notes}` : '')
      : `Paid in full — GH¢ ${money(r.shown)}`}>
      <Box sx={{
        px: 0.4, py: 0.25, borderRadius: 1, textAlign: 'center', minWidth: 50,
        bgcolor: r.tone.bg,
        border: `1px solid ${owing ? r.tone.fg : r.tone.line}`,
      }}>
        <Typography sx={{ fontSize: 7.5, fontWeight: 800, color: r.tone.fg, lineHeight: 1.2 }}>
          {r.tone.word}
        </Typography>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: r.tone.fg, lineHeight: 1.25, ...TNUM }}>
          {money(owing ? r.short : r.shown)}
        </Typography>
      </Box>
    </Tooltip>
  );
}

export default function CreditorsWeekDialog({
  open, onClose, date, className, incomeType, onFilterSheet,
}) {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('md'));

  const anchorWeek = () => mondayOf(date ? new Date(`${date}T00:00:00`) : new Date());

  const [draftFrom, setDraftFrom] = useState(() => isoOf(anchorWeek()));
  const [draftTo,   setDraftTo]   = useState(() => isoOf(addDays(anchorWeek(), 4)));
  const [range, setRange] = useState(() => ({
    from: isoOf(anchorWeek()), to: isoOf(addDays(anchorWeek(), 4)),
  }));

  const [cls, setCls] = useState(className || '');
  const [type, setType] = useState(incomeType || '');
  const [sheet, setSheet] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [onlyOwing, setOnlyOwing] = useState(false);
  const [exportAnchor, setExportAnchor] = useState(null);

  // Re-anchor on the day being marked each time it is opened, so the
  // period shown is the one the teacher is working in.
  useEffect(() => {
    if (!open) return;
    const m = anchorWeek();
    setDraftFrom(isoOf(m)); setDraftTo(isoOf(addDays(m, 4)));
    setRange({ from: isoOf(m), to: isoOf(addDays(m, 4)) });
    setCls(className || ''); setType(incomeType || '');
    setOnlyOwing(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, date, className, incomeType]);

  const load = useCallback(async () => {
    if (!open || !cls) return;
    setLoading(true); setError('');
    try {
      const r = await API.get('/collections/me/week-sheet', {
        params: {
          from: range.from, to: range.to, class_name: cls,
          ...(type ? { income_type: type } : {}),
        },
      });
      setSheet(r.data || null);
    } catch (e) {
      setError(e?.response?.data?.message || 'Could not load the period');
      setSheet(null);
    } finally { setLoading(false); }
  }, [open, cls, type, range]);

  useEffect(() => { load(); }, [load]);

  const classes  = useMemo(() => sheet?.classes || [], [sheet]);
  const allTypes = useMemo(() => sheet?.available_types || [], [sheet]);
  const types    = useMemo(() => sheet?.income_types || [], [sheet]);
  const allDays  = useMemo(() => sheet?.days || [], [sheet]);

  const single = !!type || types.length === 1;
  const soleType = type || (types.length === 1 ? types[0] : '');
  const title = soleType ? `${prettyType(soleType)} Creditors` : 'Collection Creditors';

  // ── Range controls ──────────────────────────────────────────────
  const apply = (from, to) => {
    if (to < from) { const t = from; from = to; to = t; }
    if (daysBetween(from, to) > MAX_DAYS - 1) {
      to = isoOf(addDays(new Date(`${from}T00:00:00`), MAX_DAYS - 1));
      swal.info(`A creditors report covers at most ${MAX_DAYS} days.`);
    }
    setDraftFrom(from); setDraftTo(to); setRange({ from, to });
  };
  const thisWeek = () => {
    const m = mondayOf(new Date());
    apply(isoOf(m), isoOf(addDays(m, 4)));
  };
  const thisMonth = () => {
    const n = new Date();
    apply(isoOf(new Date(n.getFullYear(), n.getMonth(), 1)),
          isoOf(new Date(n.getFullYear(), n.getMonth() + 1, 0)));
  };
  // Step by however long the current window is, so ‹ › walks weeks on a
  // week and months on a month.
  const step = (dir) => {
    const span = daysBetween(range.from, range.to) + 1;
    apply(isoOf(addDays(new Date(`${range.from}T00:00:00`), dir * span)),
          isoOf(addDays(new Date(`${range.to}T00:00:00`),   dir * span)));
  };

  // Past a week, days nobody marked are dead columns. Drop them, or a
  // month of 31 headings buries the fortnight that has data in it.
  const days = useMemo(() => {
    if (allDays.length <= 7) return allDays;
    const used = new Set();
    for (const s of sheet?.students || []) {
      for (const k of Object.keys(s.cells || {})) used.add(String(k).split('|')[1]);
    }
    const kept = allDays.filter(d => used.has(d));
    return kept.length ? kept : allDays;
  }, [allDays, sheet]);

  // ── One row block per learner ───────────────────────────────────
  const rows = useMemo(() => {
    const out = [];
    for (const s of sheet?.students || []) {
      const mine = types.filter(t => s.types?.[t]);
      if (!mine.length) continue;

      let owing = 0, periodShort = 0, creditDays = 0, notEaten = 0, paidDays = 0;
      const perDay = {};
      const notes = [];

      for (const t of mine) {
        owing += Number(s.types[t].arrears || 0);
        const rate = Number(s.types[t].daily_amount || 0);
        for (const d of days) {
          const r = readCell(s.cells?.[`${t}|${d}`], rate);
          if (!r) continue;
          const { dow } = dayLabel(d);
          if (r.tone === CELL.absent) { notEaten += 1; notes.push(`Absent ${dow}`); continue; }
          if (r.short > 0.009) {
            periodShort += r.short; creditDays += 1;
            perDay[d] = (perDay[d] || 0) + r.short;
            notes.push(r.tone === CELL.partial ? `Part ${dow}` : 'Payment pending');
          } else paidDays += 1;
        }
      }

      // Say each distinct thing once — "Payment pending" three times is
      // not three pieces of information.
      const remark = [...new Set(notes)].slice(0, 3).join(', ') || '-';
      out.push({
        s, mine, owing, periodShort, creditDays, notEaten, paidDays, perDay, remark,
        isCreditor: owing > 0.009 || periodShort > 0.009,
      });
    }
    // Worst first, then alphabetical — the teacher reads the top of the
    // list and stops.
    return out.sort((a, b) =>
      (b.owing + b.periodShort) - (a.owing + a.periodShort)
      || String(a.s.student_name).localeCompare(String(b.s.student_name)));
  }, [sheet, types, days]);

  const shown = useMemo(
    () => (onlyOwing ? rows.filter(r => r.isCreditor) : rows), [rows, onlyOwing]);

  const totals = useMemo(() => ({
    students: rows.length,
    creditors: rows.filter(r => r.isCreditor).length,
    owing: rows.reduce((a, r) => a + r.owing, 0),
    period: rows.reduce((a, r) => a + r.periodShort, 0),
    days: rows.reduce((a, r) => a + r.creditDays, 0),
  }), [rows]);

  const perDayTotals = useMemo(() => {
    const m = {};
    for (const d of days) m[d] = rows.reduce((a, r) => a + (r.perDay[d] || 0), 0);
    return m;
  }, [rows, days]);

  // ── Print ───────────────────────────────────────────────────────
  const printReport = () => {
    const head = days.map(d => {
      const { dow, dm } = dayLabel(d);
      return `<th>${dow}<br><span class="sm">${dm}</span></th>`;
    }).join('');

    const body = shown.map((r, i) => r.mine.map((t, ti) => {
      const cells = days.map(d => {
        const x = readCell(r.s.cells?.[`${t}|${d}`], r.s.types[t].daily_amount);
        if (!x) return `<td class="c">·</td>`;
        const owed = x.short > 0.009;
        return `<td class="c"><b>${x.tone.word}</b><br>${money(owed ? x.short : x.shown)}</td>`;
      }).join('');
      const span = r.mine.length;
      return `<tr>
        ${ti === 0 ? `<td rowspan="${span}">${i + 1}</td>
                      <td rowspan="${span}" class="nm">${r.s.student_name}</td>
                      <td rowspan="${span}">${r.s.class_name || ''}</td>` : ''}
        ${single ? '' : `<td>${prettyType(t)}</td>`}
        ${cells}
        ${ti === 0 ? `<td rowspan="${span}" class="r"><b>${money(r.owing)}</b></td>
                      <td rowspan="${span}">${r.isCreditor ? 'Creditor' : 'Not Owing'}</td>
                      <td rowspan="${span}" class="lf">${r.remark}</td>` : ''}
      </tr>`;
    }).join('')).join('');

    const html = `<!doctype html><html><head><meta charset="utf-8">
      <title>${title} — ${cls}</title>
      <style>
        @page { size: A4 landscape; margin: 10mm; }
        body { font-family: Arial, Helvetica, sans-serif; color:#111; }
        h1 { font-size: 15pt; margin:0 0 2px; }
        .sub { font-size: 9pt; color:#555; margin-bottom: 8px; }
        table { width:100%; border-collapse: collapse; }
        th, td { border:1px solid #999; padding:3px 4px; font-size:8pt; text-align:center; }
        th { background:#eaf2fb; }
        .sm { font-weight: normal; font-size: 7pt; color:#555; }
        .nm { text-align:left; font-weight:bold; }
        .lf { text-align:left; }
        .r { text-align:right; }
        .c { font-size: 7.5pt; }
        tfoot td { font-weight:bold; background:#fdf8ee; }
        .tot { margin-top:10px; font-size:9pt; }
        .tot b { display:inline-block; min-width:190px; }
      </style></head><body>
      <h1>${title} — ${cls}</h1>
      <div class="sub">${rangeLabel(range.from, range.to)}
        &middot; printed ${new Date().toLocaleString()}</div>
      <table>
        <thead><tr>
          <th>#</th><th>Student Name</th><th>Class</th>
          ${single ? '' : '<th>Collection</th>'}
          ${head}
          <th>Total Owing<br><span class="sm">(GH¢)</span></th>
          <th>Status</th><th>Remarks</th>
        </tr></thead>
        <tbody>${body}</tbody>
        <tfoot><tr>
          <td colspan="${(single ? 3 : 4)}">Owed per day</td>
          ${days.map(d => `<td>${perDayTotals[d] ? money(perDayTotals[d]) : '-'}</td>`).join('')}
          <td class="r">${money(totals.owing)}</td><td colspan="2"></td>
        </tr></tfoot>
      </table>
      <div class="tot">
        <div><b>Total students</b> ${totals.students}</div>
        <div><b>Creditors</b> ${totals.creditors}</div>
        <div><b>Owed in this period</b> GH¢ ${money(totals.period)}</div>
        <div><b>Total amount owing</b> GH¢ ${money(totals.owing)}</div>
      </div></body></html>`;

    const w = window.open('', '_blank');
    if (!w) { swal.warning('Allow pop-ups to print this report.'); return; }
    w.document.write(html); w.document.close(); w.focus();
    setTimeout(() => w.print(), 300);
  };

  // ── Excel ───────────────────────────────────────────────────────
  const exportExcel = async () => {
    try {
      const wb = new ExcelJS.Workbook();
      wb.created = new Date();
      const ws = wb.addWorksheet(String(cls || 'Creditors').slice(0, 28), {
        pageSetup: { orientation: 'landscape', fitToPage: true, fitToWidth: 1, fitToHeight: 0 },
      });

      ws.addRow([`${title} — ${cls}`]).font = { bold: true, size: 14 };
      ws.addRow([rangeLabel(range.from, range.to)]).font = { italic: true, size: 10 };
      ws.addRow([]);

      const header = ['#', 'Student Name', 'Class',
        ...(single ? [] : ['Collection']),
        ...days.map(d => { const { dow, dm } = dayLabel(d); return `${dow} ${dm}`; }),
        'Owed this period (GH¢)', 'Total Owing (GH¢)', 'Status', 'Remarks'];
      const hr = ws.addRow(header);
      hr.font = { bold: true };
      hr.eachCell(c => {
        c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFEAF2FB' } };
        c.alignment = { horizontal: 'center', wrapText: true };
        c.border = { top: { style: 'thin' }, left: { style: 'thin' },
                     bottom: { style: 'thin' }, right: { style: 'thin' } };
      });

      for (const [i, r] of shown.entries()) {
        for (const [ti, t] of r.mine.entries()) {
          const cells = days.map(d => {
            const x = readCell(r.s.cells?.[`${t}|${d}`], r.s.types[t].daily_amount);
            if (!x) return '·';
            return `${x.tone.word} ${money(x.short > 0.009 ? x.short : x.shown)}`;
          });
          const row = ws.addRow([
            ti === 0 ? i + 1 : '',
            ti === 0 ? r.s.student_name : '',
            ti === 0 ? (r.s.class_name || '') : '',
            ...(single ? [] : [prettyType(t)]),
            ...cells,
            ti === 0 ? Number(r.periodShort.toFixed(2)) : '',
            ti === 0 ? Number(r.owing.toFixed(2)) : '',
            ti === 0 ? (r.isCreditor ? 'Creditor' : 'Not Owing') : '',
            ti === 0 ? r.remark : '',
          ]);
          row.eachCell(c => {
            c.border = { top: { style: 'hair' }, left: { style: 'hair' },
                         bottom: { style: 'hair' }, right: { style: 'hair' } };
          });
        }
      }

      ws.addRow([]);
      ws.addRow(['Total students', totals.students]).font = { bold: true };
      ws.addRow(['Creditors', totals.creditors]);
      ws.addRow(['Owed in this period (GH¢)', Number(totals.period.toFixed(2))]);
      ws.addRow(['Total amount owing (GH¢)', Number(totals.owing.toFixed(2))]);

      ws.columns.forEach((c, i) => { c.width = i === 1 ? 28 : (i === header.length - 1 ? 26 : 13); });
      ws.views = [{ state: 'frozen', ySplit: 4 }];

      const buf = await wb.xlsx.writeBuffer();
      const blob = new Blob([buf], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${title}-${cls}-${range.from}`.replace(/[^A-Za-z0-9_-]+/g, '_') + '.xlsx';
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      console.error(e);
      swal.error(e?.message || 'Excel export failed');
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xl" fullScreen={isPhone}>

      {/* ══ Header: what, whose class, which dates ════════════════ */}
      <DialogTitle sx={{ p: 0 }}>
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.4}
          alignItems={{ lg: 'center' }} justifyContent="space-between"
          sx={{ px: 2.2, py: 1.6, bgcolor: '#fff', borderBottom: '1px solid #e8eef6' }}>

          <Stack direction="row" spacing={1.6} alignItems="center">
            <Box sx={{
              width: 46, height: 46, borderRadius: 2.5, flexShrink: 0,
              bgcolor: '#2f6fb0', color: '#fff',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <CreditorsIcon sx={{ fontSize: 25 }} />
            </Box>
            <Box>
              <Typography sx={{ fontSize: 20, fontWeight: 800, color: '#1e3a6b', lineHeight: 1.2 }}>
                {title}
              </Typography>
              <Typography sx={{ fontSize: 11.5, color: '#64748b' }}>
                See and manage students with outstanding fees.
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
            {allTypes.length > 1 && (
              <TextField select size="small" label="Collection" value={type}
                onChange={(e) => setType(e.target.value)} sx={{ minWidth: 142 }}>
                <MenuItem value="">All collections</MenuItem>
                {allTypes.map(t => <MenuItem key={t} value={t}>{prettyType(t)}</MenuItem>)}
              </TextField>
            )}
            <TextField select size="small" label="Class" value={cls}
              onChange={(e) => setCls(e.target.value)} sx={{ minWidth: 132 }}>
              {classes.length === 0 && <MenuItem value={cls || ''}>{cls || '—'}</MenuItem>}
              {classes.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </TextField>
            <TextField size="small" type="date" label="From" value={draftFrom}
              onChange={(e) => setDraftFrom(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 152 }} />
            <TextField size="small" type="date" label="To" value={draftTo}
              onChange={(e) => setDraftTo(e.target.value)}
              InputLabelProps={{ shrink: true }} sx={{ minWidth: 152 }} />
            <Button size="small" variant="contained" disableElevation
              onClick={() => apply(draftFrom, draftTo)}
              sx={{
                textTransform: 'none', fontWeight: 700, borderRadius: 1.5, px: 1.8,
                bgcolor: '#2f6fb0', '&:hover': { bgcolor: '#1e3a6b' },
              }}>
              Show
            </Button>
            <IconButton size="small" onClick={onClose}><CloseIcon fontSize="small" /></IconButton>
          </Stack>
        </Stack>

        {/* The period, and the two spans a school actually asks for. */}
        <Stack direction="row" spacing={0.4} alignItems="center" flexWrap="wrap" useFlexGap
          sx={{ px: 2.2, py: 0.9, bgcolor: '#f7fafd', borderBottom: '1px solid #e8eef6' }}>
          <IconButton size="small" onClick={() => step(-1)}><ChevronLeft fontSize="small" /></IconButton>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#334155', px: 0.5 }}>
            {rangeLabel(range.from, range.to)}
          </Typography>
          <IconButton size="small" onClick={() => step(1)}><ChevronRight fontSize="small" /></IconButton>
          <Tooltip title="Back to this week">
            <IconButton size="small" onClick={thisWeek}><TodayIcon fontSize="small" /></IconButton>
          </Tooltip>
          <Chip size="small" clickable label="This week" onClick={thisWeek}
            sx={{ height: 22, fontSize: 10.5, fontWeight: 700, ml: 0.5 }} />
          <Chip size="small" clickable label="This month" onClick={thisMonth}
            sx={{ height: 22, fontSize: 10.5, fontWeight: 700 }} />
          <Box sx={{ flex: 1 }} />
          <Chip size="small" clickable
            label={onlyOwing ? 'Showing only creditors' : 'Showing everyone'}
            onClick={() => setOnlyOwing(v => !v)}
            icon={<FilterIcon sx={{ fontSize: 14 }} />}
            sx={{
              height: 24, fontSize: 10.5, fontWeight: 700,
              bgcolor: onlyOwing ? '#fdf2e0' : '#fff',
              color: onlyOwing ? '#a1610a' : '#475569',
              border: `1px solid ${onlyOwing ? '#f3ddb8' : '#dce7f5'}`,
            }} />
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.2, md: 2 }, bgcolor: '#fbfcfe' }}>
        {error && <Alert severity="error" sx={{ mb: 1.4 }}>{error}</Alert>}

        {/* What the sheet means, in one sentence. */}
        <Card elevation={0} sx={{
          p: 1.4, mb: 1.6, borderRadius: 2.5,
          bgcolor: '#f1f6fd', border: '1px solid #dce7f5',
        }}>
          <Stack direction="row" spacing={1.3} alignItems="flex-start">
            <InfoIcon sx={{ fontSize: 19, color: '#2f6fb0', mt: 0.1 }} />
            <Box>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#1e3a6b' }}>
                Creditors are students who have not fully paid
                {soleType ? ` their ${prettyType(soleType).toLowerCase()} fees` : ''}.
              </Typography>
              <Typography sx={{ fontSize: 11, color: '#64748b' }}>
                Each cell is one day. A cell with an outline owes money — the figure
                shown is what is still owed for that day, not what was paid.
                {!single && ' A student on more than one collection gets a line for each.'}
              </Typography>
            </Box>
          </Stack>
        </Card>

        {loading ? (
          <Box sx={{ py: 8, textAlign: 'center' }}><CircularProgress /></Box>
        ) : shown.length === 0 ? (
          <Card elevation={0} sx={{
            p: 5, textAlign: 'center', borderRadius: 2.5,
            bgcolor: '#f1faf5', border: '1px solid #cfe7d6',
          }}>
            <Typography sx={{ fontSize: 15, fontWeight: 800, color: '#15803d' }}>
              {rows.length === 0
                ? `Nothing registered for ${cls || 'this class'} in these dates`
                : `Nobody in ${cls || 'this class'} is owing`}
            </Typography>
            <Typography sx={{ fontSize: 12, color: '#64748b', mt: 0.5 }}>
              {rows.length === 0
                ? 'Try a different class or a wider range of dates.'
                : 'Every learner who took it has paid for it.'}
            </Typography>
          </Card>
        ) : (
          <Card elevation={0} sx={{
            borderRadius: 2.5, overflow: 'hidden',
            border: '1px solid #e3eaf3', bgcolor: '#fff',
          }}>
            <TableContainer sx={{ maxHeight: { xs: 'none', md: '54vh' } }}>
              <Table size="small" stickyHeader sx={{
                borderCollapse: 'separate', borderSpacing: 0,
                '& th': {
                  fontWeight: 800, fontSize: 10.5, lineHeight: 1.25, py: 0.8, px: 1,
                  position: { xs: 'static', md: 'sticky' },
                },
                '& td': {
                  fontSize: 11.5, py: 0.3, px: 1, color: '#1e293b',
                  borderBottom: '1px solid #eef2f7',
                  borderRight: '1px solid #eef2f7',
                },
                '& td:last-of-type': { borderRight: 0 },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 34 }}>#</TableCell>
                    <TableCell sx={{ minWidth: 166 }}>Student Name</TableCell>
                    <TableCell sx={{ width: 74 }}>Class</TableCell>
                    {!single && <TableCell sx={{ width: 94 }}>Collection</TableCell>}
                    {days.map(d => {
                      const { dow, dm } = dayLabel(d);
                      return (
                        <TableCell key={d} align="center" sx={{ minWidth: 64 }}>
                          {dow}
                          <Box component="span" sx={{
                            display: 'block', fontWeight: 600, fontSize: 9, opacity: .8,
                          }}>
                            {dm}
                          </Box>
                        </TableCell>
                      );
                    })}
                    <TableCell align="center" sx={{ width: 86 }}>Total Owing<br />(GH¢)</TableCell>
                    <TableCell align="center" sx={{ width: 96 }}>Status</TableCell>
                    <TableCell sx={{ minWidth: 120 }}>Remarks</TableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {shown.map((r, i) => {
                    const n = Math.max(1, r.mine.length);
                    return r.mine.map((t, ti) => (
                      <TableRow key={`${r.s.student_id}|${t}`}
                        sx={{ '&:hover td': { bgcolor: '#f4f8fd' } }}>
                        {ti === 0 && (
                          <>
                            <TableCell rowSpan={n} sx={{
                              color: '#94a3b8', fontWeight: 600,
                              verticalAlign: 'middle', ...TNUM,
                              borderBottom: '1px solid #e2e9f2',
                            }}>
                              {i + 1}
                            </TableCell>
                            <TableCell rowSpan={n} sx={{
                              verticalAlign: 'middle',
                              borderBottom: '1px solid #e2e9f2',
                            }}>
                              <Typography sx={{ fontSize: 12, fontWeight: 700, color: '#0f172a' }}>
                                {r.s.student_name}
                              </Typography>
                              <Typography sx={{ fontSize: 9, color: '#94a3b8' }}>
                                {r.creditDays > 0
                                  ? `${r.creditDays} day${r.creditDays === 1 ? '' : 's'} on credit`
                                  : `${r.paidDays} day${r.paidDays === 1 ? '' : 's'} paid`}
                                {r.notEaten > 0 ? ` · ${r.notEaten} not taken` : ''}
                              </Typography>
                            </TableCell>
                            <TableCell rowSpan={n} sx={{
                              fontWeight: 600, color: '#334155',
                              verticalAlign: 'middle',
                              borderBottom: '1px solid #e2e9f2',
                            }}>
                              {r.s.class_name}
                            </TableCell>
                          </>
                        )}

                        {!single && (
                          <TableCell sx={{
                            borderBottom: ti === n - 1 ? '1px solid #e2e9f2' : undefined,
                          }}>
                            <Chip size="small" label={prettyType(t)}
                              sx={{
                                height: 18, fontSize: 9, fontWeight: 700,
                                '& .MuiChip-label': { px: 0.8 },
                                bgcolor: '#eef4fb', color: '#1c5b96',
                                border: '1px solid #dce7f5',
                              }} />
                          </TableCell>
                        )}

                        {days.map(d => (
                          <TableCell key={d} align="center" sx={{
                            p: 0.3,
                            borderBottom: ti === n - 1 ? '1px solid #e2e9f2' : undefined,
                          }}>
                            <Cell mark={r.s.cells?.[`${t}|${d}`]}
                              rate={r.s.types[t].daily_amount} />
                          </TableCell>
                        ))}

                        {ti === 0 && (
                          <>
                            <TableCell rowSpan={n} align="center" sx={{
                              verticalAlign: 'middle', ...TNUM,
                              fontSize: 14, fontWeight: 800,
                              color: r.owing > 0.009 ? '#b91c1c' : '#94a3b8',
                              borderBottom: '1px solid #e2e9f2',
                            }}>
                              <Tooltip title={r.periodShort > 0.009
                                ? `GH¢ ${money(r.periodShort)} of it was added in these dates`
                                : 'Nothing added in these dates'}>
                                <Box component="span">{money(r.owing)}</Box>
                              </Tooltip>
                            </TableCell>
                            <TableCell rowSpan={n} align="center" sx={{
                              verticalAlign: 'middle',
                              borderBottom: '1px solid #e2e9f2',
                            }}>
                              <Chip size="small"
                                label={r.isCreditor ? 'Creditor' : 'Not Owing'}
                                sx={{
                                  height: 23, minWidth: 84, borderRadius: 999,
                                  fontSize: 10.5, fontWeight: 700, border: 'none',
                                  bgcolor: r.isCreditor ? '#fdf0d8' : '#dff3e7',
                                  color:   r.isCreditor ? '#a1610a' : '#15803d',
                                }} />
                            </TableCell>
                            <TableCell rowSpan={n} sx={{
                              verticalAlign: 'middle', fontSize: 10.5, color: '#475569',
                              borderBottom: '1px solid #e2e9f2',
                            }}>
                              {r.remark}
                            </TableCell>
                          </>
                        )}
                      </TableRow>
                    ));
                  })}
                </TableBody>

                {/* What each day of the period cost in credit. Inside the
                    table, so it cannot drift out of line with the
                    columns it is totalling. */}
                <TableBody>
                  <TableRow sx={{ bgcolor: '#fdf8ee' }}>
                    <TableCell colSpan={single ? 3 : 4} sx={{
                      fontSize: 10.5, fontWeight: 800, color: '#a16207',
                      borderTop: '2px solid #f3e0ba',
                    }}>
                      OWED PER DAY
                    </TableCell>
                    {days.map(d => (
                      <TableCell key={d} align="center" sx={{
                        fontSize: 10.5, fontWeight: 800, color: '#b45309', ...TNUM,
                        borderTop: '2px solid #f3e0ba',
                      }}>
                        {perDayTotals[d] ? money(perDayTotals[d]) : '—'}
                      </TableCell>
                    ))}
                    <TableCell align="center" sx={{
                      fontSize: 12, fontWeight: 800, color: '#b91c1c', ...TNUM,
                      borderTop: '2px solid #f3e0ba',
                    }}>
                      {money(totals.owing)}
                    </TableCell>
                    <TableCell colSpan={2} sx={{ borderTop: '2px solid #f3e0ba' }} />
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
        )}

        {/* ══ Summary and legend ═════════════════════════════════ */}
        <Stack direction={{ xs: 'column', lg: 'row' }} spacing={1.4} sx={{ mt: 1.6 }}>
          <Card elevation={0} sx={{
            p: 1.8, borderRadius: 2.5, flex: '1 1 340px',
            bgcolor: '#f2f7fd', border: '1px solid #dce7f5',
          }}>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Box sx={{
                width: 38, height: 38, borderRadius: '50%', flexShrink: 0,
                bgcolor: '#2f6fb0', color: '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>
                <CreditorsIcon sx={{ fontSize: 20 }} />
              </Box>
              <Typography sx={{
                fontSize: 12, fontWeight: 800, color: '#1e3a6b',
                textTransform: 'uppercase', letterSpacing: .4,
              }}>
                Creditors<br />Summary
              </Typography>
              {[
                ['Total Students', totals.students, '#0f172a'],
                ['Total Owing', totals.creditors, '#a16207'],
                ['Owed this period', `GH¢ ${money(totals.period)}`, '#b45309'],
                ['Total Amount Owing', `GH¢ ${money(totals.owing)}`, '#b91c1c'],
              ].map(([label, value, colour]) => (
                <Box key={label} sx={{ px: 1.4, borderLeft: '1px solid #dce7f5' }}>
                  <Typography sx={{ fontSize: 10, fontWeight: 600, color: '#64748b' }}>
                    {label}
                  </Typography>
                  <Typography sx={{ fontSize: 17, fontWeight: 800, color: colour, ...TNUM }}>
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Card>

          <Card elevation={0} sx={{
            p: 1.8, borderRadius: 2.5, flex: '1 1 260px',
            bgcolor: '#fff', border: '1px solid #e3eaf3',
          }}>
            <Typography sx={{ fontSize: 12, fontWeight: 800, color: '#1e3a6b', mb: 0.9 }}>
              Legend
            </Typography>
            <Stack spacing={0.6}>
              {[
                [CELL.full.fg,    'Paid',      'taken and paid for'],
                [CELL.none.fg,    'Credit',    'taken, not paid — this is a debt'],
                [CELL.partial.fg, 'Part',      'some paid, the rest owed'],
                [CELL.absent.fg,  'Not Eaten', 'did not take it — nothing owed'],
                [CELL.advance.fg, 'Advance',   'already paid for, before the day'],
                [BLANK.fg,        '·',         'nothing marked for that day'],
              ].map(([colour, word, what]) => (
                <Stack key={word} direction="row" spacing={1} alignItems="center">
                  <Box sx={{ width: 9, height: 9, borderRadius: '50%', bgcolor: colour, flexShrink: 0 }} />
                  <Typography sx={{ fontSize: 10.5, color: '#334155' }}>
                    <b>{word}</b> — {what}
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Card>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 2, py: 1.4, borderTop: '1px solid #e8eef6' }}>
        {onFilterSheet && rows.some(r => r.isCreditor) && (
          <Button startIcon={<FilterIcon />}
            onClick={() => {
              onFilterSheet(rows.filter(r => r.isCreditor).map(r => r.s.student_id));
              onClose();
            }}
            sx={{ textTransform: 'none', fontWeight: 700, color: '#2f6fb0', mr: 'auto' }}>
            Show only creditors on the sheet
          </Button>
        )}
        <Button variant="outlined" startIcon={<PrintIcon />} onClick={printReport}
          disabled={shown.length === 0}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 1.5,
            color: '#334155', borderColor: '#d8e1ec',
          }}>
          Print Report
        </Button>
        <Button variant="outlined" startIcon={<ExcelIcon />} endIcon={<CaretIcon />}
          onClick={(e) => setExportAnchor(e.currentTarget)}
          disabled={shown.length === 0}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 1.5,
            color: '#15803d', borderColor: '#bfe5cf', bgcolor: '#f2faf5',
          }}>
          Export
        </Button>
        <Menu anchorEl={exportAnchor} open={!!exportAnchor} onClose={() => setExportAnchor(null)}>
          <MenuItem onClick={() => { setExportAnchor(null); exportExcel(); }}>
            Excel workbook (.xlsx)
          </MenuItem>
          <MenuItem onClick={() => { setExportAnchor(null); printReport(); }}>
            Print / save as PDF
          </MenuItem>
        </Menu>
        <Button variant="contained" disableElevation onClick={onClose}
          sx={{
            textTransform: 'none', fontWeight: 700, borderRadius: 1.5,
            bgcolor: '#2f6fb0', '&:hover': { bgcolor: '#1e3a6b' },
          }}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
